import styled from "styled-components";
import * as AlfredoInterfaces from "../../interfaces";
import { ate, ALVO_DE_TOQUE } from "../global/breakpoints";
import { CENTRAL, ESTRUTURA } from "../global/tokens";

import MenuItem from "@mui/material/MenuItem";
import { Divider, IconButton, Menu, Tooltip } from "@mui/material";

/*
 * A MOLDURA AMARELA É FIXA NOS DOIS TEMAS — e por isso o que fica em cima dela
 * não pode vir do `theme`.
 *
 * A barra superior e as duas laterais são o LADRILHO do ícone: `#fee97b` em
 * claro e em escuro. Sobre ladrilho cheio o sistema de design admite um texto
 * só, o carvão — o amarelo puro sobre o próprio amarelo dá 1:1, e foi isso que
 * a medição no navegador encontrou no logotipo e nos quatro ícones do topo
 * quando o tema escuro passou a usar o ladrilho como cor de seleção.
 *
 * Ação sobre a moldura é `--tinta`, o azul que aguenta o amarelo (4,0:1).
 */
const SOBRE_LADRILHO = ESTRUTURA.carvao;
const ACAO_SOBRE_LADRILHO = CENTRAL.tinta;

export const LayoutContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100vh;
`;

export const MainContent = styled.div<{
  leftbarVisible?: boolean;
  rightbarVisible?: boolean;
}>`
  flex: 1;
  display: flex;
  margin-top: calc(var(--u) * 70);
  /*
    Estes dois têm que andar junto com a LARGURA das barras
    (LeftSidebarContainer 400, RightSidebarContainer 300): é a margem que abre o
    espaço onde a barra fica. Se um escalar e o outro não, em 1920 a barra passa
    a ser mais larga que a margem e cobre o conteúdo.
  */
  margin-left: ${({ leftbarVisible }) =>
    leftbarVisible ? "calc(var(--u) * 400)" : "0"};
  margin-right: ${({ rightbarVisible }) =>
    rightbarVisible ? "calc(var(--u) * 300)" : "0"};
  transition: margin 0.3s ease;
  background-color: ${({ theme }) => theme.colors.background};
  color: ${({ theme }) => theme.colors.text};
  overflow: hidden;

  /*
    AS BARRAS SAEM DO FLUXO EM DOIS DEGRAUS, e a conta explica cada um.
    Somadas elas ocupam 700 de largura fluida.

      >= 1440  as duas empurram o conteudo. Sobram 740 para o trabalho: e o
               desenho original, no monitor em que ele foi feito, intacto.
      1280..1439  a da DIREITA vira gaveta. Ela e um painel de consulta, nao
               navegacao — some primeiro. Sobram ~880 de conteudo em 1280.
      < 1280   a da ESQUERDA tambem vira gaveta. Em 1024 as duas empurrando
               deixariam 324 de conteudo, que foi o que espremeu a Home numa
               coluna e truncou os botoes.
  */
  ${ate.amplo} {
    margin-right: 0;
  }

  ${ate.desktop} {
    margin-left: 0;
  }
`;

export const scrollContainer = styled.div`
  flex: 1;
  overflow: auto;
  background-color: ${(props) => props.theme.colors.background};
  transition: all 0.3s ease;

  &::-webkit-scrollbar {
    width: calc(var(--u) * 15);
    height: calc(var(--u) * 15);
  }

  &::-webkit-scrollbar-track {
    background: ${(props) => props.theme.colors.modalColor + "90"};
  }

  &::-webkit-scrollbar-thumb {
    background-color: ${(props) => props.theme.colors.selection};
    border-radius: 10px;
    border: 2px solid ${(props) => props.theme.colors.modalColor + "90"};
  }

  &::-webkit-scrollbar-thumb:hover {
    background-color: ${(props) => props.theme.colors.selectionDark};
    cursor: grab;
  }

  &::-webkit-scrollbar-thumb:active {
    cursor: grabbing;
  }
  &:hover {
    scrollbar-width: auto;
  }
  scrollbar-color: ${(props) => props.theme.colors.selection};
`;

export const ContentWrapper = styled.div`
  flex: 1;
  padding: var(--sp-20);
  overflow: auto;
  /* Sem isto o filho se recusa a encolher e a página inteira rola de lado. */
  min-width: 0;

  ${ate.tablet} {
    padding: var(--sp-12);
  }

  ${ate.telefone} {
    padding: var(--sp-8);
  }
`;

/**
 * O FUNDO DA GAVETA — só existe no modo sobreposto (abaixo de 1280).
 *
 * Com a barra flutuando por cima do conteúdo é preciso um jeito óbvio de
 * fechá-la sem procurar o botão do topo; e sem um fundo escurecido a barra e o
 * conteúdo se confundem, porque as duas são amarelas.
 *
 * Acima de 1280 ele não é renderizado (display: none): lá a barra divide a
 * linha com o conteúdo e não há nada a fechar.
 */
export const FundoDaGaveta = styled.div<{
  $aberto?: boolean;
  $lado: "esquerda" | "direita";
}>`
  display: none;
  position: fixed;
  inset: calc(var(--u) * 70) 0 0 0;
  background-color: rgba(0, 0, 0, 0.45);
  z-index: 99;
  border: none;
  padding: 0;
  width: 100%;
  cursor: pointer;

  /* Cada lado vira gaveta na sua largura — a mesma escada do MainContent. */
  ${({ $lado }) => ($lado === "direita" ? ate.amplo : ate.desktop)} {
    display: ${({ $aberto }) => ($aberto ? "block" : "none")};
  }
`;

/**
 * A FAIXA DE MODO DEMONSTRAÇÃO.
 *
 * Só é renderizada com `REACT_APP_DEMO=sim` (ver MainLayout.tsx) — no build de
 * produção ela não existe.
 *
 * `position: sticky` DENTRO do ContentWrapper, e não `fixed` no topo da janela,
 * por um motivo de layout: a `TopBarContainer` acima é `position: fixed` com
 * `z-index: 1000` e ocupa os primeiros 70px da tela. Uma faixa `fixed` em
 * `top: 0` cobriria o logo e o menu do usuário — justamente o que se quer
 * mostrar na apresentação. Grudada no topo da área de trabalho ela fica visível
 * o tempo todo, inclusive rolando a página, e não tapa nada.
 *
 * Vermelho de aviso da identidade da casa, texto branco: tem de ser impossível
 * confundir esta tela com a de um cliente real.
 */
export const FaixaDeDemonstracao = styled.div`
  position: sticky;
  top: calc(var(--sp-20) * -1);
  z-index: 5;
  margin: calc(var(--sp-20) * -1) calc(var(--sp-20) * -1) var(--sp-16);
  padding: var(--sp-8) var(--sp-16);
  background-color: #be3028;
  color: #ffffff;
  font-size: var(--fs-sm);
  font-weight: bold;
  letter-spacing: 0.02em;
  text-align: center;
`;

// TOP BAR COM CURVAS CONVEXAS
export const TopBarContainer = styled.div<{
  leftbarVisible?: boolean;
  rightbarVisible?: boolean;
}>`
  display: flex;
  align-items: center;
  gap: var(--sp-8);
  padding: var(--sp-10) var(--sp-20);
  background-color: #fee97b;
  color: ${({ theme }) => theme.colors.selection};
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  height: calc(var(--u) * 70);
  z-index: 1000;
  justify-content: space-between;
  box-sizing: border-box;

  ${ate.tablet} {
    padding: var(--sp-8) var(--sp-10);
  }

  /* Curva convexa para a sidebar esquerda */
  &::before {
    content: "";
    position: absolute;
    bottom: calc(var(--u) * -25);
    left: 0;
    width: calc(var(--u) * 400);
    height: calc(var(--u) * 25);
    background-color: #fee97b;
    border-bottom-right-radius: 25px;
    display: ${({ leftbarVisible }) => (leftbarVisible ? "block" : "none")};
    z-index: 999;
  }

  /* No modo gaveta a curva não tem barra embaixo para continuar: some. Cada
     uma some na largura em que a SUA barra sai do fluxo. */
  ${ate.amplo} {
    &::after {
      display: none;
    }
  }

  ${ate.desktop} {
    &::before {
      display: none;
    }
  }

  /* Curva convexa para a sidebar direita */
  &::after {
    content: "";
    position: absolute;
    bottom: calc(var(--u) * -25);
    right: 0;
    width: calc(var(--u) * 300);
    height: calc(var(--u) * 25);
    background-color: #fee97b;
    border-bottom-left-radius: 25px;
    display: ${({ rightbarVisible }) => (rightbarVisible ? "block" : "none")};
    z-index: 999;
  }
`;

// LEFT BAR COM CURVA SUPERIOR DIREITA
export const LeftSidebarContainer = styled.div<AlfredoInterfaces.SidebarProps>`
  position: fixed;
  top: calc(var(--u) * 70);
  left: 0;
  padding-top: calc(var(--u) * 25);
  transform: ${(props) =>
    props.isOpen ? "translateX(0%)" : "translateX(-100%)"};
  height: calc(100vh - calc(var(--u) * 70));
  width: calc(var(--u) * 400);
  overflow-y: hidden;
  background-color: #fee97b;
  transition: transform 0.3s ease;
  z-index: 100;
  /* A barra tem chat, menu e rodapé: em altura curta o conteúdo tem de rolar
     dentro dela em vez de vazar para fora da janela. */
  display: flex;
  flex-direction: column;

  ${ate.desktop} {
    box-shadow: 4px 0 24px rgba(0, 0, 0, 0.35);
  }

  ${ate.tablet} {
    width: min(calc(var(--u) * 400), 88vw);
  }
`;

// RIGHT BAR COM CURVA SUPERIOR ESQUERDA
export const RightSidebarContainer = styled.div<AlfredoInterfaces.SidebarProps>`
  position: fixed;
  top: calc(var(--u) * 70);
  right: 0;
  transform: ${(props) =>
    props.isOpen ? "translateX(0%)" : "translateX(100%)"};
  height: calc(100vh - calc(var(--u) * 70));
  width: calc(var(--u) * 300);
  overflow-y: auto;
  background-color: #fee97b;
  transition: transform 0.3s ease;
  z-index: 100;

  ${ate.desktop} {
    box-shadow: -4px 0 24px rgba(0, 0, 0, 0.35);
  }

  ${ate.tablet} {
    width: min(calc(var(--u) * 320), 88vw);
  }
`;

// ELEMENTOS ADICIONAIS PARA AS CURVAS (opcional - para melhor efeito visual)
export const CurveOverlayLeft = styled.div<{ isOpen?: boolean }>`
  position: fixed;
  top: calc(var(--u) * 70);
  left: calc(var(--u) * 400);
  width: calc(var(--u) * 25);
  height: calc(var(--u) * 25);
  background: transparent;
  border-top-left-radius: 50%;
  box-shadow: -12.5px -12.5px 0 12.5px #fee97b;
  z-index: 101;
  pointer-events: none;
  /* O deslocamento tem que ser o mesmo do \`left\` acima, senão a curva se
     desencontra da barra fora de 1440. */
  transform: ${({ isOpen }) =>
    isOpen ? "translateX(0)" : "translateX(calc(var(--u) * -400))"};
  transition: transform 0.3s ease;

  ${ate.desktop} {
    display: none;
  }
`;

export const CurveOverlayRight = styled.div<{ isOpen?: boolean }>`
  position: fixed;
  top: calc(var(--u) * 70);
  right: calc(var(--u) * 300);
  width: calc(var(--u) * 25);
  height: calc(var(--u) * 25);
  background: transparent;
  border-top-right-radius: 50%;
  box-shadow: 12.5px -12.5px 0 12.5px #fee97b;
  z-index: 101;
  pointer-events: none;
  /* Mesmo par do lado esquerdo: o deslocamento acompanha o \`right\` acima. */
  transform: ${({ isOpen }) =>
    isOpen ? "translateX(0)" : "translateX(calc(var(--u) * 300))"};
  transition: transform 0.3s ease;

  ${ate.amplo} {
    display: none;
  }
`;

// O restante do seu código permanece igual...
export const MaterialIcons = styled.div`
  font-family: "Material Icons";
  font-weight: normal;
  font-style: normal;
  color: ${ACAO_SOBRE_LADRILHO};
  font-size: var(--fs-5xl);
  /* 40px de ícone x4 no topo de um telefone de 375 não cabem junto com o logo.
     Aqui o tamanho cai, mas o ALVO de toque continua o do container. */
  ${ate.tablet} {
    font-size: var(--fs-3xl);
  }
  ${ate.telefone} {
    font-size: var(--fs-2xl);
  }
  text-shadow: -0.5px -0.5px 0 #000, 0.5px -0.5px 0 #000, -0.5px 0.5px 0 #000,
    1px 1px 0 #000;
`;

export const DropdownMenu = styled(Menu)`
  .MuiPaper-root {
    background-color: #f5f5f5;
    width: calc(var(--u) * 200);
    border-radius: 20px;
    display: flex;
    flex-direction: column;
    align-items: center;
  }

  .MuiMenuItem-root {
    display: flex;
    align-items: center;
    gap: var(--sp-5);
    padding: var(--sp-10) var(--sp-20);
    color: #777777;
  }

  .MuiDivider-root {
    border-color: #777777;
  }
`;

export const StyledMenuItem = styled(MenuItem)`
  display: flex;
  align-items: center;
  color: #777777;
`;

export const StyledMenuIcon = styled.div`
  font-family: "Material Icons";
  font-weight: normal;
  font-style: normal;
  font-size: var(--fs-xl);
`;

export const LogOutText = styled.p`
  color: ${(props) => props.theme.bluePrimary};
  font-weight: 400;
  font-size: var(--fs-base);
  line-height: 1.17;
  width: 100%;
  text-align: center;
`;

export const UserName = styled.span`
  position: relative;
  display: flex;
  align-items: center;
  cursor: pointer;
`;

export const IconContainer = styled.div`
  position: relative;
  width: calc(var(--u) * 50);
  height: calc(var(--u) * 50);
  display: flex;
  align-items: center;
  cursor: pointer;
`;

export const ImgContainer = styled.div`
  position: relative;
  width: calc(var(--u) * 49);
  height: calc(var(--u) * 49);
  display: flex;
  align-items: center;
  cursor: pointer;
`;

export const StyledImg = styled.img`
  width: 100%;
  height: 100%;
  border-radius: 50%;
`;

export const StyledImage = styled.img`
  width: calc(var(--u) * 40);
  height: calc(var(--u) * 40);
  cursor: pointer;
  transition: transform 0.2s;

  &:hover {
    transform: scale(1.05);
  }
`;

export const StyledIconBackground = styled.div`
  position: absolute;
  background-color: ${(props) => props.theme.black};
  border-radius: 50%;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 1;
`;

export const StyledIcon = styled.div`
  position: absolute;
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const MainItemText = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;

  h3 {
    font-weight: 400;
    font-size: var(--fs-xl);
    line-height: 1.17;
  }

  p {
    font-weight: 300;
    font-size: var(--fs-md);
    line-height: 1.17;
  }
`;

export const RegularItemText = styled.p`
  color: #777777;
  font-weight: 300;
  font-size: var(--fs-base);
  line-height: 1.17;
  width: 100%;
`;

export const OptionsContainer = styled.div`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: var(--sp-10);
  margin-right: var(--sp-10);

  ${ate.tablet} {
    gap: var(--sp-4);
    margin-right: 0;
  }
`;

export const SearchContainer = styled.div`
  position: relative;
  min-width: 0;
  flex: 1 1 auto;
  max-width: calc(var(--u) * 420);

  /* No telefone a busca sai do topo: com logo + 4 ícones + campo não sobra
     largura para nenhum deles, e era o campo que empurrava tudo para fora da
     tela. A busca continua alcançável pelo menu. */
  ${ate.telefone} {
    display: none;
  }
`;

export const HeaderContainer = styled.div`
  position: sticky;
  top: 0;
  z-index: 10;
  background-color: inherit;
`;

export const TopBarMaterialIcons = styled.div<{ selected?: boolean }>`
  font-family: "Material Icons";
  font-weight: normal;
  font-style: normal;
  /* Acompanha o texto do item (fs-lg), em vez de 150% do corpo. */
  font-size: var(--fs-xl);
  flex-shrink: 0;
  /* Este ícone mora DENTRO da pastilha do item escolhido, então segue a mesma
     regra dela: solto pisa no ladrilho (carvão), escolhido pisa na superfície
     do conteúdo (a cor de ação do tema). Ver PageButton. */
  color: ${(props) =>
    props.selected ? props.theme.colors.selection : SOBRE_LADRILHO};
  background: transparent;
  border-radius: 50%;
  padding: ${(props) => (props.selected ? "calc(var(--u) * 7)" : "0")};
  text-shadow: -0.5px -0.5px 0 #000, 0.5px -0.5px 0 #000, -0.5px 0.5px 0 #000,
    1px 1px 0 #000;
`;

export const SidebarTitle = styled.h1`
  font-size: calc(var(--u) * 45);
  z-index: 10;
  color: ${ACAO_SOBRE_LADRILHO};
  text-align: center;
  text-shadow: -0.5px -0.5px 0 #000, 0.5px -0.5px 0 #000, -0.5px 0.5px 0 #000,
    1px 1px 0 #000;
  padding: var(--sp-20);
  background-color: transparent;
  /* "alfredo central" numa linha só. Abaixo de 1040 a unidade fluida congela
     (o piso de escala.ts), então em 375 os 45u continuavam valendo 36px e o
     nome quebrava em duas linhas gigantes por cima do resto do topo. */
  white-space: nowrap;

  ${ate.laptop} {
    font-size: calc(var(--u) * 34);
    padding: var(--sp-10) var(--sp-12);
  }

  ${ate.tablet} {
    font-size: calc(var(--u) * 26);
    padding: var(--sp-8);
  }

  ${ate.telefone} {
    font-size: calc(var(--u) * 22);
    padding: var(--sp-4);
  }
`;

export const RightSidebarTitle = styled.h1`
  font-size: var(--fs-2xl);
  position: sticky;
  top: 0;
  z-index: 10;
  margin: 0;
  padding: var(--sp-15);
  background-color: inherit;
  color: ${({ theme }) => theme.colors.selection};
  text-align: center;
  text-shadow: -0.5px -0.5px 0 #000, 0.5px -0.5px 0 #000, -0.5px 0.5px 0 #000,
    1px 1px 0 #000;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

/*
 * OS ITENS DO MENU — densidade.
 *
 * Estavam com `font-size: 150%` (herdando o corpo, dava ~24 em 1440) e recuo em
 * PIXEL FIXO (`20 + nível * 50`). Dois efeitos: cada linha do menu ocupava
 * ~56 de altura, e o terceiro nível começava a 120px da borda numa barra de
 * 400 — sobrava menos da metade da largura para o texto do item.
 *
 * Agora: tamanho na escada tipográfica (fs-lg = 18 em 1440), altura de linha
 * enxuta e recuo em unidade fluida. O item continua sendo um alvo de toque
 * legítimo por `min-height`, não por padding acidental.
 */
export const PageButton = styled.button<{ selected?: boolean; level?: number }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--sp-8);
  /*
   * O ITEM ESCOLHIDO É A PRÓPRIA SUPERFÍCIE DO CONTEÚDO, e não um branco fixo.
   *
   * Era CENTRAL.papel (#FFFEFB), um quase-branco que ignora o tema. O
   * resultado quebrava a leitura nos dois lados: no claro, uma pastilha BRANCA
   * sobre a página creme; no escuro — onde salta aos olhos — uma pastilha
   * BRANCA apontando para um conteúdo #252525.
   *
   * A forma já dizia o que ela queria ser: border-radius 50px 0 0 50px
   * arredonda só a esquerda, ou seja, ela é uma ABA que encosta no painel. Uma
   * aba que não tem a cor do painel não encosta em nada — vira um adesivo
   * solto. Pintando-a com theme.colors.background, a pastilha passa a ser o
   * conteúdo ENTRANDO na barra: creme no claro, tela no escuro, sempre igual ao
   * que está do outro lado da borda.
   */
  background: ${({ selected, theme }) =>
    selected ? theme.colors.background : "transparent"};
  padding: var(--sp-6) var(--sp-8);
  padding-left: ${({ level }) => `calc(var(--u) * ${16 + (level || 0) * 26})`};
  width: 100%;
  min-height: ${ALVO_DE_TOQUE};
  text-align: left;
  border: none;
  border-radius: 50px 0 0 50px;
  cursor: pointer;
  /*
   * O texto acompanha a superfície que está debaixo dele.
   *
   * Solto, ele pisa no ladrilho amarelo, e ali o único texto que passa é o
   * carvão (regra 2 do sistema de design). Escolhido, ele pisa na superfície do
   * conteúdo, e quem manda ali é a cor de ação DO TEMA: selection é a tinta
   * azul no claro e o ladrilho amarelo no escuro. Com o ACAO_SOBRE_LADRILHO
   * fixo de antes, o item escolhido no tema escuro ficaria azul-escuro sobre
   * #252525.
   */
  color: ${({ selected, theme }) =>
    selected ? theme.colors.selection : SOBRE_LADRILHO};
  font-size: var(--fs-lg);
  line-height: 1.15;
  font-weight: bold;
  text-shadow: -0.5px -0.5px 0 #000, 0.5px -0.5px 0 #000, -0.5px 0.5px 0 #000,
    1px 1px 0 #000;

  ${ate.tablet} {
    font-size: var(--fs-base);
  }
`;

export const RightPageButton = styled.button<{
  selected?: boolean;
  level?: number;
}>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: ${({ selected, theme }) =>
    selected ? theme.colors.background : "transparent"};
  padding: var(--sp-12) var(--sp-8);
  padding-left: ${({ level }) => `${15 + (level || 0) * 20}px`};
  width: 100%;
  text-align: left;
  border: none;
  border-radius: 0 50px 50px 0;
  cursor: pointer;
  color: ${({ selected, theme }) =>
    selected ? theme.colors.selection : "#333"};
  font-size: var(--fs-base);
  font-weight: bold;
  text-shadow: -0.5px -0.5px 0 #000, 0.5px -0.5px 0 #000, -0.5px 0.5px 0 #000,
    1px 1px 0 #000;
  margin: var(--sp-5) 0;
  transition: all 0.2s ease;

  &:hover {
    background: ${({ theme }) => theme.colors.background};
    color: ${({ theme }) => theme.colors.selection};
  }
`;

/**
 * O CHAT DA BARRA ESQUERDA — de posicionamento fixo para fluxo normal.
 *
 * O que havia: PromptContainer com position: fixed; bottom: 0; width: 100%, e
 * um MessagesContainer cuja altura era calculada em JavaScript
 * (window.innerHeight - 418, com 10px somados por menu aberto). Funcionava por
 * acidente — o transform da barra faz dela o bloco de contenção do fixed — e
 * quebrava em qualquer altura de janela fora da conta: a barra de perguntas
 * saía por baixo do rodapé em tela curta.
 *
 * Agora a barra é uma coluna flex e o chat é o último filho com margin-top:
 * auto. Ele gruda embaixo sozinho, em qualquer altura, sem nenhuma medida em
 * JavaScript.
 */
export const ChatContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  margin-top: auto;
  min-height: 0;
  background-color: ${(props) => props.theme.colors.background};
`;

export const MessagesContainer = styled.div`
  flex: 1 1 auto;
  min-height: calc(var(--u) * 80);
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  padding: var(--sp-8);
  background: ${(props) => props.theme.colors.background};
  border: 1px solid ${(props) => props.theme.colors.border}55;
`;

/** O convite que aparece antes da primeira pergunta. */
export const ChatVazio = styled.div`
  padding: var(--sp-10);
  font-size: var(--fs-2xs);
  line-height: 1.35;
  color: ${(props) => props.theme.colors.label};
`;

/**
 * De onde a resposta saiu. Existe porque uma resposta em linguagem natural
 * sobre dinheiro precisa ser conferível: o rodapé diz qual relatório o
 * assistente abriu, e o usuário pode abrir o mesmo e comparar.
 */
export const FontesDaResposta = styled.div`
  margin-top: var(--sp-6);
  padding-top: var(--sp-4);
  border-top: 1px solid rgba(0, 0, 0, 0.15);
  font-size: var(--fs-3xs);
  opacity: 0.75;
  text-shadow: none;
`;

export const Message = styled.div<{ sender: "user" | "gpt" }>`
  margin: var(--sp-4) 0;
  padding: var(--sp-8);
  border-radius: 8px;
  /*
   * Balão do TEMA. Eram dois cinzas fixos com o texto na cor de seleção e
   * contorno preto — no tema escuro isso virava amarelo com sombra preta sobre
   * cinza-claro, ilegível nos dois sentidos. Agora a pergunta é a superfície
   * elevada e a resposta é a superfície do cartão, com o texto do tema.
   */
  background-color: ${(props) =>
    props.sender === "user"
      ? props.theme.colors.primary
      : props.theme.colors.background};
  border: 1px solid ${(props) => props.theme.colors.border}55;
  color: ${(props) => props.theme.colors.text};
  align-self: ${(props) =>
    props.sender === "user" ? "flex-end" : "flex-start"};
  max-width: 92%;
  font-size: var(--fs-2xs);
  line-height: 1.35;
  /* Resposta de IA tem parágrafo, lista e número: sem isto o texto saía numa
     linha só e vazava a barra. */
  white-space: pre-wrap;
  overflow-wrap: anywhere;
`;

export const PromptContainer = styled.div`
  background-color: ${(props) => props.theme.colors.background};
  width: 100%;
  box-sizing: border-box;
  flex: 0 0 auto;
`;

export const InputContainer = styled.div`
  backdrop-filter: blur(10px);
  border: 2px solid rgba(224, 224, 224, 0.5);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  display: flex;
  align-items: center;
  padding: var(--sp-8);
  background: rgba(0, 0, 0, 0.1);
`;

export const MessageInput = styled.textarea`
  background: ${(props) => props.theme.colors.primary};
  color: ${(props) => props.theme.colors.text};
  flex-grow: 1;
  margin-right: var(--sp-8);
  padding: var(--sp-8);
  border-radius: 4px;
  border: 1px solid ${(props) => props.theme.colors.border};
  resize: none;
  &:focus {
    outline: none;
    border-color: ${(props) => props.theme.colors.selection};
  }
`;

export const ChatButton = styled.button`
  background-color: ${(props) => props.theme.colors.selection};
  color: ${(props) => props.theme.colors.background};
  border: 1px solid ${(props) => props.theme.colors.selection};
  padding: var(--sp-15);
  border-radius: 4px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: "Material Icons";
  font-size: var(--fs-3xl);
  min-width: ${ALVO_DE_TOQUE};
  min-height: ${ALVO_DE_TOQUE};

  &:hover:not(:disabled) {
    background-color: #ffffffdd;
    color: ${(props) => props.theme.colors.selection};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

export const TemplatesContainer = styled.div`
  display: grid;
  /* Três sugestões cabem lado a lado na barra de 400; abaixo disso elas
     descem, uma por linha, em vez de encolher até virar reticências. */
  grid-template-columns: repeat(auto-fit, minmax(calc(var(--u) * 108), 1fr));
  gap: var(--sp-8);
  padding: var(--sp-8);
  background: rgba(0, 0, 0, 0.2);
`;

/**
 * OS BOTÕES DE SUGESTÃO DA IA — o sintoma mais visível do problema de tamanho.
 *
 * O que havia: altura FIXA de 50, -webkit-line-clamp: 2, e um segundo texto
 * escondido (.full-text) que aparecia num balão fora do botão a partir de
 * 600px. Em cima disso, LeftBar.tsx rodava adjustTextForTwoLines, que cortava
 * a string LETRA A LETRA e colava reticências até caber. Resultado na tela do
 * Guilherme: "Quantas compras pend...", "Qual saldo de massa no esto...",
 * "Qual faturamento d...".
 *
 * Agora: altura automática, texto quebrando em quantas linhas precisar, e
 * nenhum corte — nem em CSS nem em JavaScript. O balão .full-text não é mais
 * necessário e saiu junto com o corte.
 */
export const TemplateButton = styled.button`
  display: block;
  width: 100%;
  min-height: ${ALVO_DE_TOQUE};
  padding: var(--sp-8);
  /* Superfície do TEMA, não branco fixo. O chat é uma "tela" dentro do
     ladrilho amarelo: no tema escuro ele é escuro, e um botão branco com o
     texto claro do tema em cima ficava branco-sobre-branco. */
  background-color: ${(props) => props.theme.colors.primary};
  color: ${(props) => props.theme.colors.text};
  border: 1px solid ${(props) => props.theme.colors.border};
  border-radius: 6px;
  cursor: pointer;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  font-size: var(--fs-2xs);
  line-height: 1.25;
  text-align: left;
  white-space: normal;
  overflow-wrap: anywhere;
  hyphens: auto;

  &:hover {
    border-color: ${(props) => props.theme.colors.selection};
    color: ${(props) => props.theme.colors.selection};
  }
`;

export const MenuContainer = styled.div`
  display: flex;
  align-items: center;
  gap: var(--sp-10);
`;

export const StyledDivider = styled(Divider)`
  border-color: #777777;
`;

export const StyledIconButton = styled(IconButton)``;

export const StyledTooltip = styled(Tooltip)``;

export const AlfredoCentralContainer = styled.div<{ leftbarVisible?: boolean }>`
  position: fixed;
  bottom: calc(var(--u) * 20);
  /* Acompanha a LARGURA REAL da barra (400), não 30vw: em 1920 os 30vw davam
     576 e o botão flutuava longe da barra; em 1024 davam 307 e ele ficava por
     cima dela. */
  left: ${({ leftbarVisible }) =>
    leftbarVisible
      ? "calc(var(--u) * 420)"
      : "calc(var(--u) * 20)"};
  z-index: 1000;
  transition: left 0.3s ease;
`;

export const StoreStatusIndicator = styled.div<{
  status: "online" | "offline" | "warning";
}>`
  width: calc(var(--u) * 8);
  height: calc(var(--u) * 8);
  border-radius: 50%;
  background-color: ${(props) =>
    props.status === "online"
      ? "#4CAF50"
      : props.status === "offline"
      ? "#F44336"
      : "#FF9800"};
  margin-right: var(--sp-8);
`;

export const RealTimeDataCard = styled.div`
  background: rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  padding: var(--sp-15);
  margin: var(--sp-10);
  border: 1px solid rgba(255, 255, 255, 0.2);
`;

export const MetricItem = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: var(--sp-8);
  padding: var(--sp-5);
  background: rgba(255, 255, 255, 0.2);
  border-radius: 4px;
`;
